var MediaScroller_1;
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { componentStyles } from './media-scroller.css.js';
/**
 * A custom web component that creates a scrollable container for media items.
 *
 * This component is designed to be used with `<ul>` and `<li>` elements within
 * a named slot "content". It dynamically calculates the number of items in the
 * list and updates the `numOfItems` property.
 *
 * @element media-scroller
 * @slot headline - A slot for the headline of the media scroller.
 * @slot content - A slot for the content of the media scroller, expected to be a `<ul>` list.
 */
let MediaScroller = MediaScroller_1 = class MediaScroller extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The number of items in the media scroller content.
         *
         * This property is automatically updated when the content slot changes.
         */
        this.numOfItems = 0;
    }
    /**
     * Scroll forwards to the next cards
     */
    scrollNext() {
        this._cardContainer.scrollLeft += this._cardContainer.offsetWidth * MediaScroller_1._scrollForce;
        this._dispatchScrollEvent();
    }
    /**
     * Scroll back to the previous cards
     */
    scrollPrev() {
        this._cardContainer.scrollLeft -= this._cardContainer.offsetWidth * MediaScroller_1._scrollForce;
        this._dispatchScrollEvent();
    }
    /**
     * The method responsible for actually rendering the template of the element to the screen.
     */
    render() {
        return html `
    <header>
      <slot name="headline"></slot>
      <div class="rhs">
        <p class="item-count"><a href="${this.linkPath}">Alle anzeigen (${this.numOfItems})</a></p>
        <div class="form-controls">
          <button @click=${this.scrollPrev}>
            <svg width="24" height="24" viewBox="0 0 33 24" fill="none" xmlns="http://www.w3.org/2000/svg" title="Back">
              <path d="M33 12H3" stroke="currentColor" stroke-width="4"></path>
              <path d="M13 2L3 12L13 22" stroke="currentColor" stroke-width="4"></path>
            </svg>
          </button>
          <button @click=${this.scrollNext}>
            <svg width="24" height="24" viewBox="0 0 33 24" fill="none" xmlns="http://www.w3.org/2000/svg" title="Next">
              <path d="M0 12L30 12" stroke="currentColor" stroke-width="4"></path>
              <path d="M20 22L30 12L20 2" stroke="currentColor" stroke-width="4"></path>
            </svg>
          </button>
        </div>
      </div>
    </header>
    <slot name="content" @slotchange=${this._onContentChange}></slot>
    `;
    }
    /**
     * Event handler for the `slotchange` event on the content slot.
     *
     * This method is called when the content of the slot changes (e.g., when
     * items are added or removed from the list). It updates the `numOfItems`
     * property based on the current number of list items.
     */
    _onContentChange() {
        this.numOfItems = this.contentItems.length;
        this.requestUpdate();
    }
    /**
     * Gets the list items from the content slot.
     *
     * This method assumes that the content slot contains a single `<ul>` element.
     * It returns a NodeList of the `<li>` elements within the list.
     * @returns {NodeList} The list items.
     */
    get contentItems() {
        const slot = this.shadowRoot.querySelector('slot[name=content]');
        const listContainer = slot.assignedElements().filter((node) => node.matches('ul'));
        if (listContainer.length > 0) {
            return listContainer[0].querySelectorAll('li');
        }
        else {
            return {};
        }
    }
    /**
     * Gets the unordered list from inside the content slot.
     *
     * This method assumes that the content slot contains a single `<ul>` element.
     * @returns {HTMLUListElement} The unordered list containing the scrollable elements.
     */
    get _cardContainer() {
        const slot = this.shadowRoot.querySelector('slot[name=content]');
        const listContainer = slot.assignedElements().filter((node) => node.matches('ul'));
        return listContainer[0];
    }
    /**
     * Fires an `Event` which bubbles up the DOM in order to allow
     * other code to react to whenever a user clicks on the scroll buttons if needed.
     *
     * @fires {Event} An event with the name `scroll-media-items`.
     */
    _dispatchScrollEvent() {
        let containerContents = '';
        if (this.linkPath.startsWith('/articles')) {
            containerContents = 'articles';
        }
        if (this.linkPath.startsWith('/category/interviews/')) {
            containerContents = 'interviews';
        }
        this.dispatchEvent(new CustomEvent('scroll-media-items', { bubbles: true, composed: true, detail: containerContents }));
    }
    get linkPath() {
        // If there is no attribute or its not a relative path
        if (this.dataset.link === undefined || !this.dataset.link.startsWith('/')) {
            return '#';
        }
        else {
            return this.dataset.link;
        }
    }
};
/**
 * The CSS styles for the media scroller component.
 */
MediaScroller.styles = componentStyles;
/**
 * A variable to control the scenario when a user clicks the next or previous buttons
 * how far we should actually scroll through the container.
 *
 * For example: a value of `0.8` translates to `80%` of the container width.
 */
MediaScroller._scrollForce = 0.8;
__decorate([
    property({ type: Number })
], MediaScroller.prototype, "numOfItems", void 0);
MediaScroller = MediaScroller_1 = __decorate([
    customElement('media-scroller')
], MediaScroller);
export { MediaScroller };
//# sourceMappingURL=media-scroller.js.map